import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CalendarView = ({
  selectedDate,
  onDateSelect,
  availabilityData,
  onDayStatusChange,
  onMonthChange
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)?.getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)?.getDay();
  
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const notifyMonthChange = (month) => {
    if (!onMonthChange) return;

    const start = new Date(month.getFullYear(), month.getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];

    onMonthChange({ startDate: start, endDate: end });
  };

  useEffect(() => {
    notifyMonthChange(currentMonth);
  }, [currentMonth]);


  const getDayStatus = (day) => {
    const dateKey = `${currentMonth?.getFullYear()}-${String(currentMonth?.getMonth() + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
    const dayData = availabilityData?.[dateKey];

    if (!dayData) return 'unavailable';
    if (dayData?.blocked) return 'blocked';
    if (dayData?.fullyBooked) return 'fully-booked';
    if (dayData?.hasSlots) return 'available';
    if (dayData?.dateSlotId) return 'active-no-slots';
    return 'unavailable';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-success/20 text-success border-success/30';
      case 'active-no-slots':
        return 'bg-primary/20 ttext-muted-foreground border-primary/30';
      case 'fully-booked':
        return 'bg-warning/20 text-warning border-warning/30';
      case 'blocked':
        return 'bg-error/20 text-error border-error/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today?.getDate() && 
           currentMonth?.getMonth() === today?.getMonth() && 
           currentMonth?.getFullYear() === today?.getFullYear();
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return day === selectedDate?.getDate() && 
           currentMonth?.getMonth() === selectedDate?.getMonth() && 
           currentMonth?.getFullYear() === selectedDate?.getFullYear();
  };

  const handleDayClick = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onDateSelect(newDate);
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const day = i - firstDayOfMonth + 1;
      
      if (i < firstDayOfMonth || day > daysInMonth) {
        days?.push(
          <div key={i} className="aspect-square p-1 md:p-2"></div>
        );
      } else {
        const status = getDayStatus(day);
        const statusColor = getStatusColor(status);
        const today = isToday(day);
        const selected = isSelected(day);

        days?.push(
          <button
            key={i}
            onClick={() => handleDayClick(day)}
            className={`aspect-square p-1 md:p-2 rounded-lg border-2 transition-all duration-250 ease-elegant hover:scale-105 active:scale-95 ${statusColor} ${
              today ? 'ring-2 ring-primary ring-offset-2' : ''
            } ${
              selected ? 'shadow-lg scale-105' : ''
            }`}
            aria-label={`${day} ${monthNames?.[currentMonth?.getMonth()]} ${currentMonth?.getFullYear()} - ${status}`}
          >
            <div className="flex flex-col items-center justify-center h-full">
              <span className="text-xs md:text-sm lg:text-base font-caption font-medium">{day}</span>
              <div className="flex gap-0.5 mt-0.5 md:mt-1">
                {status === 'available' && <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-success"></div>}
                {status === 'fully-booked' && <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-warning"></div>}
                {status === 'blocked' && <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-error"></div>}
              </div>
            </div>
          </button>
        );
      }
    }

    return days;
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-muted rounded-lg transition-all duration-250 ease-elegant hover:scale-105 active:scale-95"
          aria-label="Previous month"
        >
          <Icon name="ChevronLeft" size={20} />
        </button>

        <h2 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground">
          {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h2>

        <button
          onClick={nextMonth}
          className="p-2 hover:bg-muted rounded-lg transition-all duration-250 ease-elegant hover:scale-105 active:scale-95"
          aria-label="Next month"
        >
          <Icon name="ChevronRight" size={20} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-3">
        {dayNames?.map((day) => (
          <div key={day} className="text-center text-xs md:text-sm font-caption font-medium text-muted-foreground py-1 md:py-2">
            <span className="hidden md:inline">{day}</span>
            <span className="md:hidden">{day?.charAt(0)}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {renderCalendarDays()}
      </div>
      <div className="flex flex-wrap gap-3 md:gap-4 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-success"></div>
          <span className="text-xs md:text-sm font-caption text-foreground">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-primary/20 border border-primary/30"></div>
          <span className="text-xs md:text-sm font-caption text-foreground">Active (No Slots Yet)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-warning"></div>
          <span className="text-xs md:text-sm font-caption text-foreground">Fully Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-error"></div>
          <span className="text-xs md:text-sm font-caption text-foreground">Blocked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-muted"></div>
          <span className="text-xs md:text-sm font-caption text-foreground">Unavailable</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;